import { AsyncPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { MetaDefinition } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import {
    ContentfulService,
    CustomPageMapping,
} from '@core/services/contentful.service';
import { MetadataService } from '@core/services/metadata.service';
import { SeoService } from '@core/services/seo.service';
import { fixAssetUrl } from '@features/contentful/helpers/assetHelpers';
import { AssetImageConfiguration } from '@features/lazy-image/components/lazy-asset-lazy/lazy-asset-lazy.component';
import {
    Configurations,
    CropFocus,
    Stops,
} from '@features/lazy-image/components/lazy-picture/lazy-picture.component';
import { TripsItinerarySectionDirective } from '@features/trips/directives/trips-itinerary-section.directive';
import { TripsItineraryMenuService } from '@features/trips/services/trips-itinerary-menu.service';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import { CustomPage, Itinerary, TextBlock } from '@generated/graphql';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { Product, WithContext } from 'schema-dts';
import { WithLoadingPipe } from '../../../../shared/with-loading/with-loading.pipe';
import { TripsItineraryGalleryComponent } from '../trips-itinerary-gallery/trips-itinerary-gallery.component';

import { BuilderIOService } from '@core/services/builder-i-o.service';
import { BuilderPageFallbackComponent } from '@features/builder/components/builder-page-fallback/builder-page-fallback.component';
import { HomeFaqNewsletterSectionComponent } from '@features/home/components/home-faq-newsletter-section/home-faq-newsletter-section.component';
import { HomeReviewsComponent } from '@features/home/components/home-reviews/home-reviews.component';
import { HomeWhyFollowaliceComponent } from '@features/home/components/home-why-followalice/home-why-followalice.component';
import { PreselectTripEnquiryModalService } from '@features/navigation/services/preselect-trip-enquiry-modal.service';
import { JsonLdComponent } from '@features/seo/json-ld/json-ld.component';
import { TripsInclusionsExclusionsSectionComponent } from '@features/trips/trips-inclusions-exclusions-section/trips-inclusions-exclusions-section.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { LoaderComponent } from '@shared/loader/loader.component';
import { derivedAsync } from 'ngxtension/derived-async';
import { RichTextDocumentComponent } from '../../../contentful-dynamic/rich-text/rich-text-document/rich-text-document.component';
import { LazyAssetComponent } from '../../../lazy-image/components/lazy-asset/lazy-asset.component';
import { LazyPictureComponent } from '../../../lazy-image/components/lazy-picture/lazy-picture.component';
import { TripsItinerarySectionDirective as TripsItinerarySectionDirective_1 } from '../../directives/trips-itinerary-section.directive';
import { TripsItineraryAccommodationComponent } from '../trips-itinerary-accommodation/trips-itinerary-accommodation.component';
import { TripsItineraryBreadcrumbsComponent } from '../trips-itinerary-breadcrumbs/trips-itinerary-breadcrumbs.component';
import { TripsItineraryDayComponent } from '../trips-itinerary-day/trips-itinerary-day.component';
import { TripsItineraryNavigationComponent } from '../trips-itinerary-navigation/trips-itinerary-navigation.component';
import { TripsTripSelfBookingComponent } from '../trips-trip-self-booking/trips-trip-self-booking.component';

@Component({
    selector: 'app-trips-itinerary-detail',
    templateUrl: './trips-itinerary-detail.component.html',
    styleUrls: ['./trips-itinerary-detail.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        LoaderComponent,
        LazyPictureComponent,
        TripsItineraryBreadcrumbsComponent,
        FontAwesomeModule,
        RichTextDocumentComponent,
        TripsItineraryNavigationComponent,
        TripsItinerarySectionDirective_1,
        LazyAssetComponent,
        TripsItineraryDayComponent,
        TripsItineraryAccommodationComponent,
        TripsItineraryGalleryComponent,
        AsyncPipe,
        WithLoadingPipe,
        JsonLdComponent,
        HomeFaqNewsletterSectionComponent,
        TripsTripSelfBookingComponent,
        HomeReviewsComponent,
        TripsInclusionsExclusionsSectionComponent,
        HomeWhyFollowaliceComponent,
        BuilderPageFallbackComponent,
    ],
})
export class TripsItineraryDetailComponent implements OnInit {
    readonly id = CustomPageMapping.HomePage;
    itinerary$: Observable<Itinerary>;

    loading = new ReplaySubject<boolean>(1);
    featuredImageConfiguration: Observable<Configurations>;
    faClock = faClock;
    mapConfiguration: Observable<AssetImageConfiguration>;
    seoProduct: Observable<WithContext<Product>>;

    @ViewChild('selfBookingDeparturesContainer')
    selfBookingDeparturesContainer;
    @ViewChildren(TripsItinerarySectionDirective)
    set itinerarySections(sections: QueryList<TripsItinerarySectionDirective>) {
        this.tripsItineraryMenuService.updateMenuItems(sections.toArray());
    }

    homepageData: Observable<CustomPage>;
    why: Observable<TextBlock[]>;
    whyDesc: Observable<TextBlock>;
    private router = inject(Router);
    private builderIOService = inject(BuilderIOService);

    builderContent = derivedAsync(() =>
        this.builderIOService.fetchCurrentPage()
    );

    generateMetaTagsForService = (data: Itinerary): MetaDefinition[] => {
        return [
            {
                name: 'description',
                content: data.seoDescription || data.shortDescription,
            },
            {
                property: 'og:description',
                content: data.seoDescription || data.shortDescription,
            },
            {
                property: 'og:type',
                content: 'website',
            },
            {
                property: 'og:image',
                content: `${fixAssetUrl(
                    data.featuredImage.url
                )}?w=1200&h=800&f=center&fit=fill&fm=jpg`,
            },
            {
                property: 'og:image:width',
                content: '1200',
            },
            {
                property: 'og:image:height',
                content: '800',
            },
        ];
    };

    constructor(
        private activatedRoute: ActivatedRoute,
        private contentfulService: ContentfulService,
        private seoService: SeoService,
        private tripsItineraryMenuService: TripsItineraryMenuService,
        private metadataService: MetadataService,
        private preselectTripEnquiryModalService: PreselectTripEnquiryModalService
    ) {}

    ngOnInit(): void {
        this.itinerary$ = this.activatedRoute.params.pipe(
            tap(() => this.loading.next(true)),
            switchMap(params =>
                combineLatest([
                    of(params),
                    this.contentfulService.itinerary(params.itinerary),
                ])
            ),
            tap(() => this.loading.next(false)),
            tap(([params, data]) => {
                if (params.slug !== data.trip.urlSlug) {
                    this.router.navigate([
                        `/adventure-trips/${data.trip.urlSlug}/${data.urlSlug}`,
                    ]);
                }
            }),
            map(([params, data]) => data),
            tap(data => {
                this.preselectTripEnquiryModalService.updateSelectedTrip(
                    data.trip
                );
                if (data.seoTitle) {
                    this.metadataService.setTitle(data.seoTitle);
                } else {
                    this.metadataService.setTitle(data.title);
                }
                this.generateMetaTagsForService(data).forEach(metaObj => {
                    this.metadataService.setAttribute(metaObj);
                });
                this.metadataService.setCanonicalUrl(this.router.url);
            }),
            shareReplay(1)
        );

        this.homepageData = this.contentfulService.customPage(this.id).pipe(
            map(customPageResponse => customPageResponse.data.customPage),
            shareReplay(1)
        );

        this.why = this.homepageData.pipe(
            map(whyFA => whyFA.imageTextBlocks1Collection.items)
        );

        this.whyDesc = this.homepageData.pipe(
            map(whyFA => {
                return whyFA.singleTextBlock2;
            })
        );

        this.featuredImageConfiguration = this.itinerary$.pipe(
            map(itinerary => ({
                [Stops.FALLBACK]: {
                    image: itinerary.featuredImage,
                    ratioHeight: 1,
                    ratioWidth: 1,
                    width: 1200,
                    width2x: 1200,
                    focus:
                        (itinerary.featuredImageCropFocus as CropFocus) ||
                        'center',
                },
                [Stops.BASE]: {
                    image: itinerary.featuredImage,
                    ratioHeight: 1,
                    ratioWidth: 1,
                    width: 600,
                    width2x: 800,
                    width3x: 1200,
                    focus:
                        (itinerary.featuredImageCropFocus as CropFocus) ||
                        'center',
                },
                [Stops.SM]: {
                    image: itinerary.featuredImage,
                    ratioHeight: 1,
                    ratioWidth: 1,
                    width: 800,
                    width2x: 1200,
                    width3x: 1600,
                    focus:
                        (itinerary.featuredImageCropFocus as CropFocus) ||
                        'center',
                },
                [Stops.MD]: {
                    image: itinerary.featuredImage,
                    ratioHeight: 2,
                    ratioWidth: 3,
                    width: 800,
                    width2x: 1600,
                    focus:
                        (itinerary.featuredImageCropFocus as CropFocus) ||
                        'center',
                },
                [Stops.LG]: {
                    image: itinerary.featuredImage,
                    ratioHeight: 1,
                    ratioWidth: 2,
                    width: 1024,
                    width2x: 1600,
                    focus:
                        (itinerary.featuredImageCropFocus as CropFocus) ||
                        'center',
                },
                [Stops.XL]: {
                    image: itinerary.featuredImage,
                    ratioHeight: 1,
                    ratioWidth: 2,
                    width: 1200,
                    width2x: 1600,
                    focus:
                        (itinerary.featuredImageCropFocus as CropFocus) ||
                        'center',
                },
                [Stops.XXL]: {
                    image: itinerary.featuredImage,
                    ratioHeight: 1,
                    ratioWidth: 3,
                    width: 1600,
                    width2x: 1600,
                    focus:
                        (itinerary.featuredImageCropFocus as CropFocus) ||
                        'center',
                },
            }))
        );
        this.mapConfiguration = this.itinerary$.pipe(
            map(itinerary => ({
                image: itinerary?.map,
                width: 800,
            }))
        );
        this.seoProduct = this.itinerary$.pipe(
            map(itinerary =>
                this.seoService.generateProductFromItinerary(itinerary)
            )
        );
    }
}
